import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Calendar, Plus, Pencil, Trash2, Users, Clock, MapPin, Loader2, Lock, Globe, School, Building2, Video, Search, Check, ChevronsUpDown, Mail, Phone, Eye } from 'lucide-react';

export default function AdminSectionsPage({ embedded }) {
  const { api } = useAuth();
  const [classSearch, setClassSearch] = useState('');
  const [courseSearchOpen, setCourseSearchOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');

  // Close course search dropdown on outside click
  useEffect(() => {
    if (!courseSearchOpen) return;
    const handleClick = (e) => {
      if (!e.target.closest('[data-course-search]')) setCourseSearchOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [courseSearchOpen]);
  const [sections, setSections] = useState([]);
  const [courses, setCourses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [rosterDialog, setRosterDialog] = useState({ open: false, section: null, enrollments: [], loading: false });
  const [formData, setFormData] = useState({
    course: '',
    location: '',
    instructor: '',
    term: 'Spring 2025',
    room: '',
    start_date: '',
    end_date: '',
    meeting_days: [],
    start_time: '15:30',
    end_time: '17:00',
    session_duration: 90,
    total_sessions: 8,
    capacity: 20,
    price: 199.99,
    active: true,
    kit: '',
    kit_requirement: 'none',
    enrollment_restriction: 'open',
    delivery_type: 'in_person',
    meeting_link: '',
    meeting_password: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sectionsRes, coursesRes, locationsRes, instructorsRes, kitsRes] = await Promise.all([
        api.get('/sections/?active_only=false&section_type=class&page_size=5000'),
        api.get('/courses/?page_size=5000'),
        api.get('/locations/'),
        api.get('/users/?role=instructor'),
        api.get('/kits/'),
      ]);
      setSections(sectionsRes.data?.results || sectionsRes.data || []);
      setCourses(coursesRes.data?.results || coursesRes.data || []);
      setLocations(locationsRes.data?.results || locationsRes.data || []);
      setInstructors(instructorsRes.data?.results || instructorsRes.data || []);
      setKits(kitsRes.data?.results || kitsRes.data || []);
    } catch (error) {
      toast.error('Failed to load sections');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoster = async (section) => {
    setRosterDialog({ open: true, section, enrollments: [], loading: true });
    try {
      const res = await api.get(`/enrollments/?section_id=${section.id}&page_size=100`);
      const enrollments = res.data?.results || res.data || [];
      setRosterDialog(prev => ({ ...prev, enrollments, loading: false }));
    } catch {
      setRosterDialog(prev => ({ ...prev, loading: false }));
      toast.error('Failed to load roster');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        instructor: formData.instructor || null,
        location: formData.delivery_type === 'online' ? null : (formData.location || null),
        meeting_days: formData.meeting_days.length ? formData.meeting_days : ['Monday', 'Wednesday'],
        section_type: 'class',
        total_sessions: parseInt(formData.total_sessions) || 8,
        end_time: calculateEndTime(formData.start_time, formData.session_duration),
        kit_requirement_override: formData.kit_requirement || 'inherit',
      };
      delete payload.session_duration;
      delete payload.kit;
      delete payload.kit_requirement;
      
      if (editingSection) {
        await api.put(`/sections/${editingSection.id}/`, payload);
        toast.success('Session updated');
      } else {
        await api.post('/sections/', payload);
        toast.success('Session created');
      }
      
      // Update kit association on the course (if kit selected)
      if (formData.course && formData.kit) {
        try {
          await api.patch(`/courses/${formData.course}/`, {
            kit: formData.kit || null,
            kit_requirement: formData.kit_requirement || 'none',
          });
        } catch {}
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save session');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await api.delete(`/sections/${id}/`);
      toast.success('Class deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  const calculateEndTime = (startTime, durationMinutes) => {
    if (!startTime) return '';
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
  };

  const resetForm = () => {
    setFormData({
      course: '',
      location: '',
      instructor: '',
      term: 'Spring 2025',
      room: '',
      start_date: '',
      end_date: '',
      meeting_days: [],
      start_time: '15:30',
      end_time: '17:00',
      session_duration: 90,
      total_sessions: 8,
      capacity: 20,
      price: 199.99,
      active: true,
      kit: '',
      kit_requirement: 'none',
      enrollment_restriction: 'open',
      delivery_type: 'in_person',
      meeting_link: '',
      meeting_password: ''
    });
    setEditingSection(null);
  };

  const openEditDialog = (section) => {
    setEditingSection(section);
    setFormData({
      course: section.course,
      location: section.location || '',
      instructor: section.instructor || '',
      term: section.term,
      room: section.room || '',
      start_date: section.start_date,
      end_date: section.end_date,
      meeting_days: Array.isArray(section.meeting_days) ? section.meeting_days : (section.meeting_days ? [section.meeting_days] : []),
      start_time: section.start_time,
      end_time: section.end_time,
      session_duration: section.total_sessions === 1 ? 180 : (() => {
        // Infer duration from start/end time
        if (section.start_time && section.end_time) {
          const [sh, sm] = section.start_time.split(':').map(Number);
          const [eh, em] = section.end_time.split(':').map(Number);
          return (eh * 60 + em) - (sh * 60 + sm);
        }
        return 90;
      })(),
      total_sessions: section.total_sessions || 8,
      capacity: section.capacity,
      price: section.price,
      active: section.active,
      kit: (() => { const c = courses.find(x => x.id === section.course); return c?.kit || ''; })(),
      kit_requirement: (() => { const c = courses.find(x => x.id === section.course); return c?.kit_requirement || 'none'; })(),
      enrollment_restriction: section.enrollment_restriction || 'open',
      delivery_type: section.delivery_type || 'in_person',
      meeting_link: section.meeting_link || '',
      meeting_password: section.meeting_password || ''
    });
    setDialogOpen(true);
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      meeting_days: prev.meeting_days.includes(day)
        ? prev.meeting_days.filter(d => d !== day)
        : [...prev.meeting_days, day]
    }));
  };

  // Get location info to determine if restriction can be changed
  const getSelectedLocation = () => {
    return locations.find(l => l.id === formData.location);
  };

  const getRestrictionBadge = (section) => {
    if (section.enrollment_restriction === 'school_only') {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700">
          <School className="h-3 w-3 mr-1" />
          {section.restricted_to_school_name || 'School Only'}
        </Badge>
      );
    }
    if (section.enrollment_restriction === 'district_only') {
      return (
        <Badge variant="outline" className="bg-purple-50 text-purple-700">
          <Building2 className="h-3 w-3 mr-1" />
          {section.restricted_to_district_name || 'District Only'}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-green-50 text-green-700">
        <Globe className="h-3 w-3 mr-1" />
        Open
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const selectedLocation = getSelectedLocation();

  return (
    <div className="space-y-6" data-testid="admin-sections-page">
      <div className="flex justify-between items-center">
        {!embedded && (
        <div>
          <h1 className="font-heading text-2xl font-bold">Classes</h1>
          <p className="text-muted-foreground">Manage classes sessions</p>
        </div>
        )}
        <div className={embedded ? 'ml-auto' : ''}>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-section-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Class
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSection ? 'Edit Class' : 'Add New Class'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Course *</Label>
                  <div className="relative" data-course-search>
                    <button type="button" onClick={() => { setCourseSearchOpen(!courseSearchOpen); setCourseSearchQuery(''); }}
                      className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background hover:bg-accent"
                      data-testid="course-select">
                      <span className={`truncate ${formData.course ? '' : 'text-muted-foreground'}`}>
                        {formData.course ? (courses.find(c => c.id === formData.course)?.title || 'Select course') : 'Search and select a course...'}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                    </button>
                    {courseSearchOpen && (
                      <div className="absolute z-50 mt-1 w-full min-w-[400px] rounded-md border bg-popover shadow-lg">
                        <div className="p-2 border-b">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Type to search courses..."
                              value={courseSearchQuery}
                              onChange={e => setCourseSearchQuery(e.target.value)}
                              className="pl-9 h-9"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-[250px] overflow-auto p-1">
                          {courses
                            .filter(c => !courseSearchQuery || c.title.toLowerCase().includes(courseSearchQuery.toLowerCase()) || (c.category || '').toLowerCase().includes(courseSearchQuery.toLowerCase()) || (c.subcategory || '').toLowerCase().includes(courseSearchQuery.toLowerCase()))
                            .map(c => (
                              <button key={c.id} type="button"
                                className={`flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md hover:bg-accent text-left transition-colors ${formData.course === c.id ? 'bg-primary/10 text-primary font-medium' : ''}`}
                                onClick={() => { setFormData({...formData, course: c.id}); setCourseSearchOpen(false); setCourseSearchQuery(''); }}>
                                {formData.course === c.id ? <Check className="h-4 w-4 text-primary shrink-0" /> : <div className="w-4" />}
                                <span className="truncate flex-1">{c.title}</span>
                                {c.category && <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">{c.category}</span>}
                              </button>
                            ))
                          }
                          {courses.filter(c => !courseSearchQuery || c.title.toLowerCase().includes(courseSearchQuery.toLowerCase()) || (c.category || '').toLowerCase().includes(courseSearchQuery.toLowerCase())).length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">No courses match "{courseSearchQuery}"</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Location *</Label>
                  <Select value={formData.location} onValueChange={(v) => setFormData({...formData, location: v})}>
                    <SelectTrigger data-testid="location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      {locations.map(l => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name} {l.partner_school_name ? `(${l.partner_school_name})` : l.park_district_name ? `(${l.park_district_name})` : l.library_name ? `(${l.library_name})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Instructor</Label>
                  <Select value={formData.instructor || 'unassigned'} onValueChange={(v) => setFormData({...formData, instructor: v === 'unassigned' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Select instructor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {instructors.map(i => (
                        <SelectItem key={i.id} value={i.id}>{i.first_name} {i.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Term *</Label>
                  <Select value={formData.term} onValueChange={(v) => setFormData({...formData, term: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Spring 2025">Spring 2025</SelectItem>
                      <SelectItem value="Summer 2025">Summer 2025</SelectItem>
                      <SelectItem value="Fall 2025">Fall 2025</SelectItem>
                      <SelectItem value="Winter 2026">Winter 2026</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Delivery Type *</Label>
                  <Select 
                    value={formData.delivery_type} 
                    onValueChange={(v) => setFormData({...formData, delivery_type: v})}
                  >
                    <SelectTrigger data-testid="delivery-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_person">In-Person</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Start Date *</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} required />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} required />
                </div>
                <div>
                  <Label>Start Time</Label>
                  <Input type="time" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value, end_time: calculateEndTime(e.target.value, formData.session_duration)})} />
                </div>
                <div>
                  <Label>Session Duration</Label>
                  <Select value={String(formData.session_duration)} onValueChange={(v) => {
                    const dur = parseInt(v);
                    setFormData({...formData, session_duration: dur, end_time: calculateEndTime(formData.start_time, dur)});
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="60">1 Hour</SelectItem>
                      <SelectItem value="90">1.5 Hours</SelectItem>
                      <SelectItem value="120">2 Hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>End Time (auto-calculated)</Label>
                  <Input type="time" value={formData.end_time || calculateEndTime(formData.start_time, formData.session_duration)} disabled className="bg-gray-50" />
                </div>
                <div>
                  <Label>Number of Sessions</Label>
                  <Select value={String(formData.total_sessions)} onValueChange={(v) => setFormData({...formData, total_sessions: parseInt(v)})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 Sessions</SelectItem>
                      <SelectItem value="6">6 Sessions</SelectItem>
                      <SelectItem value="8">8 Sessions</SelectItem>
                      <SelectItem value="10">10 Sessions</SelectItem>
                      <SelectItem value="12">12 Sessions</SelectItem>
                      <SelectItem value="16">16 Sessions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Capacity</Label>
                  <Input type="number" value={formData.capacity} onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})} />
                </div>

                <div>
                  <Label>Room</Label>
                  <Input 
                    value={formData.room} 
                    onChange={(e) => setFormData({...formData, room: e.target.value})} 
                    placeholder="Room 101"
                    disabled={formData.delivery_type === 'online'}
                  />
                </div>
              </div>
              
              {/* Online Course Settings */}
              {(formData.delivery_type === 'online' || formData.delivery_type === 'hybrid') && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg space-y-4 border border-blue-200 dark:border-blue-800">
                  <Label className="font-semibold flex items-center gap-2 text-blue-700 dark:text-blue-300">
                    <Video className="h-4 w-4" />
                    Online Class Settings
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Meeting Link (Zoom/Google Meet)</Label>
                      <Input 
                        type="url"
                        value={formData.meeting_link} 
                        onChange={(e) => setFormData({...formData, meeting_link: e.target.value})} 
                        placeholder="https://zoom.us/j/123456789 or https://meet.google.com/xxx-xxxx-xxx"
                        data-testid="meeting-link-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Students will receive this link in their enrollment confirmation</p>
                    </div>
                    <div>
                      <Label>Meeting Password (Optional)</Label>
                      <Input 
                        value={formData.meeting_password} 
                        onChange={(e) => setFormData({...formData, meeting_password: e.target.value})} 
                        placeholder="Meeting password if required"
                        data-testid="meeting-password-input"
                      />
                    </div>
                  </div>
                  {formData.delivery_type === 'online' && (
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      💡 Online classes don't require a physical location. Students can join from anywhere!
                    </p>
                  )}
                  {formData.delivery_type === 'hybrid' && (
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      💡 Hybrid classes combine in-person and online sessions. Make sure to set both the location and meeting link.
                    </p>
                  )}
                </div>
              )}
              
              {/* Enrollment Restriction */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <Label className="font-semibold flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Enrollment Restriction
                </Label>
                <Select 
                  value={formData.enrollment_restriction} 
                  onValueChange={(v) => setFormData({...formData, enrollment_restriction: v})}
                  disabled={selectedLocation?.partner_school || selectedLocation?.park_district || selectedLocation?.library}
                >
                  <SelectTrigger data-testid="restriction-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open to All Students</SelectItem>
                    <SelectItem value="school_only">School Students Only</SelectItem>
                    <SelectItem value="district_only">District Students Only</SelectItem>
                  </SelectContent>
                </Select>
                {selectedLocation?.partner_school && (
                  <p className="text-sm text-blue-600 flex items-center gap-1">
                    <School className="h-4 w-4" />
                    Location is at a partner school - defaults to school-only enrollment
                  </p>
                )}
                {(selectedLocation?.park_district || selectedLocation?.library) && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <Globe className="h-4 w-4" />
                    Location is at a park district/library - open enrollment
                  </p>
                )}
                {!selectedLocation?.partner_school && !selectedLocation?.park_district && !selectedLocation?.library && formData.location && (
                  <p className="text-sm text-muted-foreground">
                    Independent location - you can set custom enrollment rules
                  </p>
                )}
              </div>
              
              <div>
                <Label>Meeting Days</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                    <Badge
                      key={day}
                      variant={formData.meeting_days.includes(day) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleDay(day)}
                    >
                      {day.slice(0, 3)}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-section-btn">{editingSection ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Calendar className="h-8 w-8 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-bold">{sections.length}</p>
            <p className="text-sm text-muted-foreground">Total Classes</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Video className="h-8 w-8 mx-auto mb-2 text-cyan-600" />
            <p className="text-2xl font-bold">{sections.filter(s => s.delivery_type === 'online' || s.delivery_type === 'hybrid').length}</p>
            <p className="text-sm text-muted-foreground">Online/Hybrid</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Globe className="h-8 w-8 mx-auto mb-2 text-green-600" />
            <p className="text-2xl font-bold">{sections.filter(s => s.enrollment_restriction === 'open').length}</p>
            <p className="text-sm text-muted-foreground">Open Enrollment</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <School className="h-8 w-8 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-bold">{sections.filter(s => s.enrollment_restriction === 'school_only').length}</p>
            <p className="text-sm text-muted-foreground">School Only</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Users className="h-8 w-8 mx-auto mb-2 text-purple-600" />
            <p className="text-2xl font-bold">{sections.reduce((sum, s) => sum + s.enrolled_count, 0)}</p>
            <p className="text-sm text-muted-foreground">Total Enrolled</p>
          </CardContent>
        </Card>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search classes by course, term, location, instructor..."
            value={classSearch}
            onChange={e => setClassSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {classSearch ? `${sections.filter(s => {
            const q = classSearch.toLowerCase();
            return (s.course_title || '').toLowerCase().includes(q) ||
                   (s.term || '').toLowerCase().includes(q) ||
                   (s.location_name || '').toLowerCase().includes(q) ||
                   (s.instructor_name || '').toLowerCase().includes(q) ||
                   (s.meeting_days || '').toString().toLowerCase().includes(q);
          }).length} of ${sections.length}` : `${sections.length} classes`}
        </span>
      </div>

      <Card className="card-dashboard">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Course</TableHead>
                <TableHead>Term</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Restriction</TableHead>
                <TableHead>Enrollment</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sections.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No sections found. Add your first section above.
                  </TableCell>
                </TableRow>
              ) : (
                sections.filter(s => {
                  if (!classSearch) return true;
                  const q = classSearch.toLowerCase();
                  return (s.course_title || '').toLowerCase().includes(q) ||
                         (s.term || '').toLowerCase().includes(q) ||
                         (s.location_name || '').toLowerCase().includes(q) ||
                         (s.instructor_name || '').toLowerCase().includes(q) ||
                         (s.meeting_days || '').toString().toLowerCase().includes(q);
                }).map((section) => (
                  <TableRow key={section.id} data-testid={`section-row-${section.id}`}>
                    <TableCell className="font-medium">{section.course_title}</TableCell>
                    <TableCell>{section.term}</TableCell>
                    <TableCell>
                      {section.delivery_type === 'online' ? (
                        <div className="flex items-center gap-1 text-blue-600">
                          <Video className="h-4 w-4" />
                          <span>Online</span>
                        </div>
                      ) : section.delivery_type === 'hybrid' ? (
                        <div>
                          <div className="flex items-center gap-1 text-purple-600">
                            <Video className="h-4 w-4" />
                            <span>Hybrid</span>
                          </div>
                          <div className="text-sm text-muted-foreground">{section.location_name}</div>
                        </div>
                      ) : (
                        <div>
                          <div>{section.location_name}</div>
                          {section.room && <div className="text-sm text-muted-foreground">Room: {section.room}</div>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{Array.isArray(section.meeting_days) ? section.meeting_days.join(', ') : (section.meeting_days || 'TBD')}</div>
                      <div className="text-muted-foreground">{section.start_time} - {section.end_time}</div>
                    </TableCell>
                    <TableCell>{getRestrictionBadge(section)}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => fetchRoster(section)}
                        className="flex items-center gap-1.5 hover:text-rsc-navy transition-colors cursor-pointer"
                        data-testid={`view-roster-${section.id}`}
                      >
                        <span className={`font-bold ${section.enrolled_count >= section.capacity ? 'text-red-600' : section.enrolled_count > 0 ? 'text-emerald-600' : ''}`}>
                          {section.enrolled_count}/{section.capacity}
                        </span>
                        {section.enrolled_count > 0 && <Eye className="h-3.5 w-3.5 text-muted-foreground" />}
                      </button>
                    </TableCell>
                    <TableCell>${(Number(section.price) || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={section.active ? 'default' : 'secondary'}>
                        {section.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(section)} data-testid={`edit-section-${section.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(section.id)} data-testid={`delete-section-${section.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Roster Dialog */}
      <Dialog open={rosterDialog.open} onOpenChange={(open) => !open && setRosterDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Enrolled Students — {rosterDialog.section?.course_title}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {rosterDialog.section?.location_name} · {rosterDialog.section?.term} · {rosterDialog.section?.enrolled_count}/{rosterDialog.section?.capacity} enrolled
            </p>
          </DialogHeader>
          {rosterDialog.loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : rosterDialog.enrollments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="font-medium">No students enrolled yet</p>
              <p className="text-sm">Enrollments will appear here as parents register.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Registered</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rosterDialog.enrollments.map(e => (
                  <TableRow key={e.id} data-testid={`roster-row-${e.id}`}>
                    <TableCell className="font-medium">{e.learner_name}</TableCell>
                    <TableCell>{e.learner_grade || '—'}</TableCell>
                    <TableCell>{e.parent_name || '—'}</TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        {e.parent_email && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Mail className="h-3 w-3" /> {e.parent_email}
                          </div>
                        )}
                        {e.parent_phone && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Phone className="h-3 w-3" /> {e.parent_phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={e.status === 'confirmed' ? 'default' : e.status === 'waitlisted' ? 'outline' : 'secondary'}>
                        {e.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {e.created_at ? new Date(e.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
